import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import ClientReportDialog from '@/components/ClientReportDialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Building2, Plus, Edit3, Trash2, Search, Mail, Phone, MapPin, User2, ExternalLink,
  Download, FileSpreadsheet, FileText, Lock, Image as ImageIcon, Upload, X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const CLIENT_PII_ROLES = ['Owner', 'Admin', 'Sales', 'Accounts'];
const REPORT_ROLES = ['Owner', 'Admin']; // Only Owner + Admin can view the full workflow report

export default function ClientsPage() {
  const { user, hasPerm, has } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  // Driven from server-side `effective_access` — not the role string. This
  // fixes the long-standing "Add client" bug where a user granted
  // clients_create had no Add button because the UI role-checked.
  const canCreate = has('clients_create');
  const canEditClients = has('clients_edit');
  const canDeleteClients = has('clients_delete');
  const canEdit = canCreate || canEditClients;  // show the action row at all
  const canExport = user?.role === 'Owner' || user?.role === 'Admin';
  const canViewPII = CLIENT_PII_ROLES.includes(user?.role);
  const canViewReport = REPORT_ROLES.includes(user?.role);
  const canManageLogo = user?.role === 'Owner' || user?.role === 'Admin';
  const canOpenProfile = has('client_profile'); // Owner / Admin bypass via has
  const [exportingRoster, setExportingRoster] = useState(false);
  const [clients, setClients] = useState([]);
  const [proposalsCounts, setProposalsCounts] = useState({});
  const [stats, setStats] = useState({}); // client_id -> {projects, users}
  const [q, setQ] = useState(() => sessionStorage.getItem('shine_clients_q') || '');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ company: '', name: '', email: '', phone: '', address: '' });
  // Logo state for the Edit dialog
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  // Shared report dialog
  const [reportFor, setReportFor] = useState(null);
  const [reportFormat, setReportFormat] = useState('pdf');

  // Report dialog state
  const [reportOpen, setReportOpen] = useState(null); // kept for legacy state consumers; UI dialog removed

  const load = async () => {
    const [c, p, u] = await Promise.all([
      api.get('/clients'),
      api.get('/projects').catch(() => ({ data: [] })),
      api.get('/users/all').catch(() => ({ data: [] })),
    ]);
    setClients(c.data);
    // Load proposals count map if user has permission
    if (hasPerm('proposals_view')) {
      try {
        const { data } = await api.get('/clients/proposals-counts');
        setProposalsCounts(data || {});
      } catch { /* ignore */ }
    }
    // Compute stats per client
    const s = {};
    c.data.forEach((x) => { s[x.id] = { projects: 0, users: 0 }; });
    p.data.forEach((pr) => { if (s[pr.client_id]) s[pr.client_id].projects++; });
    u.data.forEach((usr) => { if (usr.role === 'Client' && s[usr.client_id]) s[usr.client_id].users++; });
    setStats(s);
  };
  useEffect(() => { load(); }, []);

  // Persist the search text so going into a profile and clicking "Back" shows
  // the same filtered list the user left.
  useEffect(() => {
    sessionStorage.setItem('shine_clients_q', q || '');
  }, [q]);

  // Deep link ?edit=<id> opens the Edit dialog for that client (used from
  // the profile page "Edit" button).
  useEffect(() => {
    const eid = sp.get('edit');
    if (!eid || clients.length === 0) return;
    const c = clients.find((x) => x.id === eid);
    if (c) openEdit(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, sp]);

  const filtered = useMemo(() => {
    if (!q.trim()) return clients;
    const t = q.toLowerCase();
    return clients.filter((c) =>
      `${c.company} ${c.name || ''} ${c.email || ''} ${c.phone || ''}`.toLowerCase().includes(t)
    );
  }, [clients, q]);

  const openCreate = () => {
    setEditing(null);
    setForm({ company: '', name: '', email: '', phone: '', address: '' });
    setOpen(true);
  };

  // Owner/Admin-only Excel export of the full client roster (contact info included).
  const exportRoster = async () => {
    if (!canExport) return;
    setExportingRoster(true);
    try {
      // Reuse the shared axios instance so the JWT is attached automatically
      // (it lives under the `shine_token` localStorage key, not `token`).
      const res = await api.get('/clients/export', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shine_clients_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Client roster exported.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Export failed');
    } finally {
      setExportingRoster(false);
    }
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      company: c.company,
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.company.trim()) { alert('Company name is required'); return; }
    const payload = {
      company: form.company.trim(),
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
    };
    try {
      if (editing) await api.patch(`/clients/${editing.id}`, payload);
      else await api.post('/clients', payload);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save client');
      return;
    }
    setOpen(false);
    setEditing(null);
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.company}? This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete client');
      return;
    }
    load();
  };

  const initials = (s) => (s || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  // Client logo blob URLs (keyed by client id). Fetched via authenticated
  // axios because the raw GET /clients/{id}/logo endpoint requires a bearer
  // token which can't travel inside a plain <img src> request.
  const [logoUrls, setLogoUrls] = useState({});
  useEffect(() => {
    let cancelled = false;
    const next = {};
    (async () => {
      await Promise.all(
        clients
          .filter((c) => c.has_logo)
          .map(async (c) => {
            try {
              const res = await api.get(`/clients/${c.id}/logo`, { responseType: 'blob' });
              next[c.id] = URL.createObjectURL(res.data);
            } catch {
              /* ignore — fall back to initials */
            }
          }),
      );
      if (!cancelled) {
        // Revoke stale blob URLs from the previous render before swapping in.
        setLogoUrls((prev) => {
          Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-fetch whenever the set of has_logo client ids changes or an upload
    // bumps the cache-bust counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients.map((c) => `${c.id}:${c.has_logo ? 1 : 0}`).join('|'), logoCacheBust]);

  const logoUrl = (c) => logoUrls[c?.id] || null;

  // Owner / Admin only — upload a new logo for the client in the Edit dialog.
  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editing) return;
    if (file.size > 1024 * 1024) {
      toast.error('Logo must be 1 MB or smaller');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setLogoBusy(true);
    try {
      await api.post(`/clients/${editing.id}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Client logo updated');
      setEditing({ ...editing, has_logo: true });
      setClients((prev) => prev.map((x) => (x.id === editing.id ? { ...x, has_logo: true } : x)));
      setLogoCacheBust(Date.now());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Logo upload failed');
    } finally {
      setLogoBusy(false);
    }
  };

  const clearLogo = async () => {
    if (!editing || !editing.has_logo) return;
    if (!window.confirm('Remove the branded logo for this client?')) return;
    setLogoBusy(true);
    try {
      await api.delete(`/clients/${editing.id}/logo`);
      toast.success('Logo removed');
      setEditing({ ...editing, has_logo: false });
      setClients((prev) => prev.map((x) => (x.id === editing.id ? { ...x, has_logo: false } : x)));
      setLogoCacheBust(Date.now());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove logo');
    } finally {
      setLogoBusy(false);
    }
  };

  const downloadExport = async (client, fmt) => {
    try {
      const token = localStorage.getItem('shine_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/export?format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const t = await res.text();
        alert('Failed to export: ' + t);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const safe = client.company.replace(/[^a-z0-9]+/gi, '_');
      const ext = fmt === 'xlsx' ? 'xlsx' : 'csv';
      const a = document.createElement('a');
      a.href = url;
      a.download = `shine_workflow_${safe}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export error: ' + err.message);
    }
  };

  const openReport = (client) => {
    setReportOpen(client);
  };

  // Open the shared Client Report dialog from a card. Users pick dates +
  // format inside the dialog, see the item count preview, then download.
  const openClientReport = (client, fmt = 'pdf') => {
    setReportFormat(fmt);
    setReportFor(client);
  };

  // Guarded navigation: clicking a card opens the profile ONLY when the user
  // has `client_profile` permission (Owner/Admin bypass). Event-level handlers
  // on the action buttons stop propagation to avoid firing this.
  const openProfile = (client) => {
    if (!canOpenProfile) return;
    navigate(`/clients/${client.id}`);
  };

  const loadReport = async () => {
    // Legacy workflow report endpoint deleted — kept as a no-op to avoid refs elsewhere.
  };

  // (The legacy "Workflow Report" dialog + its download helper used the
  // /clients/{id}/report endpoint, which has been removed from the backend
  // because it leaked hours/team-member names into client-facing output.
  // The Client Report button on each card now uses /api/reports/client-work.)


  return (
    <div>
      <PageHeader
        icon={Building2}
        title="Clients"
        subtitle={canEdit
          ? 'Add and manage client companies. Only Owner and Digital Marketer can edit.'
          : 'View client companies (Owner / Digital Marketer can edit).'}
        actions={
          <div className="flex items-center gap-2">
            {canExport && (
              <Button
                onClick={exportRoster}
                disabled={exportingRoster}
                variant="outline"
                className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                data-testid="export-clients-btn"
                title="Export the client roster to Excel"
              >
                {exportingRoster ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
                Export
              </Button>
            )}
            {canCreate && (
              <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="new-client-btn">
                <Plus className="h-4 w-4 mr-1" /> Add client
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-slate-400">Total clients</div>
          <div className="text-2xl font-extrabold text-white mt-1">{clients.length}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-slate-400">Active projects</div>
          <div className="text-2xl font-extrabold text-white mt-1">
            {Object.values(stats).reduce((a, b) => a + (b.projects || 0), 0)}
          </div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-slate-400">Portal logins</div>
          <div className="text-2xl font-extrabold text-white mt-1">
            {Object.values(stats).reduce((a, b) => a + (b.users || 0), 0)}
          </div>
        </Card>
      </div>

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300/70" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, contact, email or phone…" className="pl-9 bg-[#0f2240] border-amber-500/20 text-white" data-testid="client-search" />
      </div>

      {/* View-only banner for roles that have no create/edit/delete rights.
          Opens Compose addressed to Owner/Admin so the user can request access. */}
      {!canEdit && (
        <Card className="shine-card p-3 rounded-xl mb-3 flex items-center gap-3 border-amber-500/40" data-testid="clients-view-only-banner">
          <Lock className="h-4 w-4 text-amber-300 flex-shrink-0" />
          <div className="text-xs text-slate-200 flex-1">
            <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-100 border border-amber-500/40 px-2 py-0.5 rounded-full text-[10px] font-semibold mr-2">
              View only
            </span>
            You can view clients but can't add, edit or delete them. Ask an admin if you need more access.
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => navigate('/messages?compose=1&access=clients')}
            data-testid="btn-request-access"
            className="border-amber-500/60 text-amber-200 hover:bg-amber-500/10"
          >
            Request access
          </Button>
        </Card>
      )}

      {filtered.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">
          {clients.length === 0 ? 'No clients yet. Click "Add client" to add your first.' : 'No clients match your search.'}
        </Card>
      ) : (
        <Card className="shine-card p-0 rounded-xl overflow-x-auto" data-testid="clients-table-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-400 bg-[#0f2240]/40 border-b border-amber-500/10">
                <th className="text-left py-2 px-3 font-medium">Client</th>
                <th className="text-left py-2 px-3 font-medium hidden md:table-cell">Contact</th>
                <th className="text-left py-2 px-3 font-medium">Projects</th>
                {hasPerm('proposals_view') && <th className="text-left py-2 px-3 font-medium">Proposals</th>}
                <th className="text-left py-2 px-3 font-medium">Logins</th>
                <th className="text-right py-2 px-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const s = stats[c.id] || { projects: 0, users: 0 };
                const rowClick = canOpenProfile ? () => openProfile(c) : undefined;
                return (
                  <tr
                    key={c.id}
                    data-testid={`client-row-${c.id}`}
                    className={`border-b border-amber-500/10 ${canOpenProfile ? 'cursor-pointer hover:bg-amber-500/5' : ''}`}
                    onClick={rowClick}
                  >
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {logoUrl(c) ? (
                          <div className="h-9 w-9 rounded-lg bg-white border border-amber-500/30 flex items-center justify-center overflow-hidden flex-shrink-0" title={`${c.company} logo`}>
                            <img src={logoUrl(c)} alt={`${c.company} logo`} className="max-h-full max-w-full object-contain" data-testid={`client-logo-${c.id}`} />
                          </div>
                        ) : (
                          <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-amber-500/15 border border-amber-500/30 flex-shrink-0">
                            <span className="text-xs font-bold text-amber-200">{initials(c.company)}</span>
                          </div>
                        )}
                        <div className="min-w-0">
                          {canOpenProfile ? (
                            <Link
                              to={`/clients/${c.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-semibold text-white hover:text-amber-300 truncate block"
                              data-testid={`client-link-${c.id}`}
                            >
                              {c.company}
                            </Link>
                          ) : (
                            <span className="font-semibold text-white truncate block">{c.company}</span>
                          )}
                          {c.name && <div className="text-[11px] text-slate-400 truncate md:hidden">{c.name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 hidden md:table-cell">
                      <div className="min-w-0">
                        {c.name && <div className="text-slate-200 truncate">{c.name}</div>}
                        {c.email && <div className="text-[11px] text-slate-400 truncate">{c.email}</div>}
                        {c._pii_redacted && (
                          <div className="text-[11px] text-slate-500 italic inline-flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Restricted
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3"><Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">{s.projects}</Badge></td>
                    {hasPerm('proposals_view') && (
                      <td className="py-2 px-3" data-testid={`client-proposals-count-${c.id}`}>
                        <Badge variant="outline" className="border-green-500/40 text-green-300 text-[10px]">
                          {proposalsCounts[c.id] || 0}
                        </Badge>
                      </td>
                    )}
                    <td className="py-2 px-3"><Badge variant="outline" className="border-sky-500/40 text-sky-300 text-[10px]">{s.users}</Badge></td>
                    <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {canViewReport && (
                          <Button size="sm" variant="ghost" onClick={() => openClientReport(c, 'pdf')} className="text-sky-300 hover:bg-sky-500/10 h-7" data-testid={`btn-client-report-${c.id}`} title="Download client-safe Work Report">
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => downloadExport(c, 'xlsx')} className="text-emerald-300 hover:bg-emerald-500/10 h-7" data-testid={`btn-xlsx-${c.id}`} title="Workflow Excel (internal)">
                            <FileSpreadsheet className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canEditClients && (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="text-amber-300 hover:bg-amber-500/10 h-7" data-testid={`btn-edit-client-${c.id}`}>
                            <Edit3 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canDeleteClients && (
                          <Button size="sm" variant="ghost" onClick={() => remove(c)} className="text-red-400 hover:bg-red-500/10 h-7" data-testid={`btn-del-client-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editing ? `Edit · ${editing.company}` : 'Add new client'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editing && (
              <div className="rounded-md border border-amber-500/20 bg-[#0f2240]/40 p-3">
                <div className="flex items-center gap-3">
                  <div className="h-16 w-16 rounded-lg bg-white border border-amber-500/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {logoUrl(editing) ? (
                      <img
                        src={logoUrl(editing)}
                        alt="Client logo preview"
                        className="max-h-full max-w-full object-contain"
                        data-testid="client-logo-preview"
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-200 font-semibold">
                      {editing.has_logo ? 'Client logo' : 'No logo uploaded yet'}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {canManageLogo
                        ? 'PNG, JPG or WEBP · up to 1 MB · auto-resized · shown top-right on monthly reports.'
                        : 'Only Owner / Admin can upload or replace a client logo.'}
                    </div>
                    {canManageLogo && (
                      <div className="mt-2 flex items-center gap-2">
                        <label
                          className="inline-flex items-center gap-1 cursor-pointer rounded-md bg-amber-500 hover:bg-amber-400 text-[#0a1628] text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"
                          data-testid="client-logo-upload"
                        >
                          {logoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                          {editing.has_logo ? 'Replace logo' : 'Upload logo'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={onLogoFile}
                            disabled={logoBusy}
                          />
                        </label>
                        {editing.has_logo && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={clearLogo}
                            disabled={logoBusy}
                            className="text-red-400 hover:bg-red-500/10 h-7 px-2"
                            data-testid="client-logo-remove"
                          >
                            <X className="h-3.5 w-3.5 mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div>
              <Label>Company name *</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="client-company" autoFocus />
            </div>
            <div>
              <Label>Primary contact name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="client-name" placeholder="e.g. Manoj Bhat" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="client-email" placeholder="contact@company.com" disabled={!canViewPII && editing} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="client-phone" placeholder="+91 9876500000" disabled={!canViewPII && editing} />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="client-address" placeholder="Office / billing address" disabled={!canViewPII && editing} />
            </div>
            {!canViewPII && (
              <div className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-md p-2 flex items-start gap-2">
                <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Email, phone &amp; address are restricted to Sales, Accounts and Admin. You can still
                  {editing ? ' update the company / contact name' : ' add a new client'} — leave PII fields blank
                  and the responsible team will fill them in.
                </span>
              </div>
            )}
            <div className="text-[11px] text-slate-400 bg-[#0f2240]/60 border border-amber-500/15 rounded-md p-2">
              Tip: To give this client portal access, head to <span className="text-amber-300">Team & Users → Add user</span>, pick role <span className="text-amber-300">Client</span>, and link them to this company.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="client-save">
              {editing ? 'Save changes' : 'Create client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClientReportDialog
        open={!!reportFor}
        onOpenChange={(v) => { if (!v) setReportFor(null); }}
        client={reportFor}
        defaultFormat={reportFormat}
      />

    </div>
  );
}
